'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function TargetHeartRateCalculator() {
  const t = useTranslations("tools.targetHeartRateCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    mhrMethod: 'age' as 'age' | 'test',
    age: 30,
    testedMhr: 180,
    rhr: 70,
    formula: 'haskell' as 'haskell' | 'tanaka' | 'nes',
    method: 'karvonen' as 'karvonen' | 'percent',
  });

  const [showSettings, setShowSettings] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const zones = [
    { name: t('zone1Name'), min: 50, max: 60, description: t('zone1Desc'), color: '#10b981' },
    { name: t('zone2Name'), min: 60, max: 70, description: t('zone2Desc'), color: '#3b82f6' },
    { name: t('zone3Name'), min: 70, max: 80, description: t('zone3Desc'), color: '#f59e0b' },
    { name: t('zone4Name'), min: 80, max: 90, description: t('zone4Desc'), color: '#ef4444' },
    { name: t('zone5Name'), min: 90, max: 100, description: t('zone5Desc'), color: '#dc2626' },
  ];

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      let mhr: number;

      if (inputs.mhrMethod === 'age') {
        const age = inputs.age;
        if (!age || age < 1 || age > 120) {
          throw new Error(t('errorInvalidAge'));
        }

        if (inputs.formula === 'haskell') {
          mhr = 220 - age;
        } else if (inputs.formula === 'tanaka') {
          mhr = 208 - (0.7 * age);
        } else {
          mhr = 211 - (0.64 * age);
        }
      } else {
        mhr = inputs.testedMhr;
        if (!mhr || mhr < 60 || mhr > 220) {
          throw new Error(t('errorInvalidMhr'));
        }
      }

      const rhr = inputs.rhr;
      if (rhr && (rhr < 30 || rhr > 120)) {
        throw new Error(t('errorInvalidRhr'));
      }

      mhr = Math.round(mhr);
      const reserve = rhr ? mhr - rhr : null;

      const calculatedZones = zones.map(zone => {
        let minHR: number, maxHR: number;

        if (inputs.method === 'karvonen' && rhr && reserve) {
          minHR = Math.round((reserve * zone.min / 100) + rhr);
          maxHR = Math.round((reserve * zone.max / 100) + rhr);
        } else {
          minHR = Math.round(mhr * zone.min / 100);
          maxHR = Math.round(mhr * zone.max / 100);
        }

        return {
          ...zone,
          minHR,
          maxHR,
        };
      });

      setResults({
        mhr,
        rhr: rhr || null,
        reserve,
        zones: calculatedZones,
      });

      setTimeout(() => {
        document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);

    } catch (err: any) {
      setError(err.message);
      setResults(null);
    }
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fbbf24 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
      overflow: 'hidden' as const,
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '900px', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)', overflow: 'hidden' as const },
    {},
    {},
    {}
  );

  const toolHeaderStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', textAlign: 'center' as const },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolTitleStyle = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formSectionStyle: React.CSSProperties = {
    background: '#fef3c7',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    border: '2px solid #fbbf24',
  };

  const formSectionTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#92400e', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e5e7eb',
      borderRadius: '0.5rem',
      transition: 'all 0.3s',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.8rem', fontSize: '1.05rem' },
    { padding: '0.875rem', fontSize: '1.1rem' }
  );

  const radioGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  };

  const radioOptionStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
  };

  const radioInputStyle: React.CSSProperties = {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#f59e0b',
  };

  const radioLabelStyle: React.CSSProperties = {
    margin: 0,
    cursor: 'pointer',
    fontWeight: 500,
  };

  const inputWithUnitStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const unitLabelStyle: React.CSSProperties = {
    color: '#6b7280',
    fontWeight: 600,
    minWidth: '40px',
  };

  const settingsToggleStyle: React.CSSProperties = {
    textAlign: 'center',
    margin: '1.5rem 0',
  };

  const settingsButtonStyle = getResponsiveStyle(
    {
      background: 'none',
      border: 'none',
      color: '#f59e0b',
      fontWeight: 600,
      cursor: 'pointer',
      padding: '0.5rem 1rem',
      borderRadius: '0.5rem',
      transition: 'all 0.3s',
    },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white',
      border: 'none',
      fontWeight: 600,
      borderRadius: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.3s',
      boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.95rem 1.75rem', fontSize: '1.05rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const errorMessageStyle: React.CSSProperties = {
    background: '#fee2e2',
    borderLeft: '4px solid #ef4444',
    padding: '1rem',
    borderRadius: '0.5rem',
    color: '#991b1b',
    marginBottom: '1rem',
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderTop: '3px solid #f59e0b',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#92400e', marginTop: 0, marginBottom: '1.5rem', textAlign: 'center' as const },
    { fontSize: '1.35rem' },
    { fontSize: '1.425rem' },
    { fontSize: '1.5rem' }
  );

  const resultCardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
  };

  const resultCardH4Style = getResponsiveStyle(
    { color: '#92400e', marginTop: 0, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const resultItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem',
    background: '#fef3c7',
    borderRadius: '0.5rem',
    marginBottom: '0.75rem',
  };

  const resultLabelStyle: React.CSSProperties = {
    fontWeight: 600,
    color: '#374151',
  };

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#f59e0b' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const zoneGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginTop: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
  );

  const zoneCardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '0.5rem',
    padding: '1rem',
    borderLeft: '4px solid #f59e0b',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
  };

  const zoneNameStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const zoneRangeStyle = getResponsiveStyle(
    { color: '#6b7280', marginBottom: '0.25rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const zoneBpmStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#f59e0b' },
    { fontSize: '0.95rem' },
    { fontSize: '0.975rem' },
    { fontSize: '1rem' }
  );

  const infoTextStyle = getResponsiveStyle(
    {
      background: '#eff6ff',
      borderLeft: '4px solid #3b82f6',
      padding: '1rem',
      borderRadius: '0.5rem',
      color: '#1e40af',
      marginTop: '1rem',
    },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: 0, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const faqItemStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.2rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' }
  );

  const refLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolTitleStyle}>{t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            {error && (
              <div style={errorMessageStyle}>
                {error}
              </div>
            )}

            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>
                <span>📊</span>
                <span>{t('mhrSectionTitle')}</span>
              </div>

              <div style={formGroupStyle}>
                <div style={radioGroupStyle}>
                  <div style={radioOptionStyle}>
                    <input
                      type="radio"
                      id="mhr-age"
                      name="mhr-method"
                      value="age"
                      checked={inputs.mhrMethod === 'age'}
                      onChange={(e) => setInputs({...inputs, mhrMethod: 'age'})}
                      style={radioInputStyle}
                    />
                    <label htmlFor="mhr-age" style={radioLabelStyle}>{t('estimateFromAge')}</label>
                  </div>
                  <div style={radioOptionStyle}>
                    <input
                      type="radio"
                      id="mhr-test"
                      name="mhr-method"
                      value="test"
                      checked={inputs.mhrMethod === 'test'}
                      onChange={(e) => setInputs({...inputs, mhrMethod: 'test'})}
                      style={radioInputStyle}
                    />
                    <label htmlFor="mhr-test" style={radioLabelStyle}>{t('useTestResult')}</label>
                  </div>
                </div>
              </div>

              {inputs.mhrMethod === 'age' ? (
                <div style={formGroupStyle}>
                  <label htmlFor="age" style={labelStyle}>{t('ageLabel')}</label>
                  <input
                    type="number"
                    id="age"
                    min="1"
                    max="120"
                    value={inputs.age}
                    onChange={(e) => setInputs({...inputs, age: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                </div>
              ) : (
                <div style={formGroupStyle}>
                  <label htmlFor="tested-mhr" style={labelStyle}>{t('testedMhrLabel')}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="tested-mhr"
                      min="60"
                      max="220"
                      value={inputs.testedMhr}
                      onChange={(e) => setInputs({...inputs, testedMhr: parseFloat(e.target.value) || 0})}
                      style={{...inputStyle, width: '100%'}}
                      required
                    />
                    <span style={unitLabelStyle}>{t('bpmUnit')}</span>
                  </div>
                </div>
              )}
            </div>

            <div style={formSectionStyle}>
              <div style={formSectionTitleStyle}>
                <span>💓</span>
                <span>{t('rhrSectionTitle')}</span>
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="rhr" style={labelStyle}>{t('rhrLabel')}</label>
                <div style={inputWithUnitStyle}>
                  <input
                    type="number"
                    id="rhr"
                    min="30"
                    max="120"
                    value={inputs.rhr}
                    onChange={(e) => setInputs({...inputs, rhr: parseFloat(e.target.value) || 0})}
                    style={{...inputStyle, width: '100%'}}
                  />
                  <span style={unitLabelStyle}>{t('bpmUnit')}</span>
                </div>
              </div>
            </div>

            <div style={settingsToggleStyle}>
              <button
                type="button"
                onClick={() => setShowSettings(!showSettings)}
                style={settingsButtonStyle}
                onMouseEnter={(e) => e.currentTarget.style.background = '#fef3c7'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                {showSettings ? t('hideSettings') : t('showSettings')}
              </button>
            </div>

            {showSettings && (
              <div>
                <div style={formSectionStyle}>
                  <div style={formSectionTitleStyle}>
                    <span>🧮</span>
                    <span>{t('formulaSectionTitle')}</span>
                  </div>

                  <div style={formGroupStyle}>
                    <div style={radioGroupStyle}>
                      <div style={radioOptionStyle}>
                        <input
                          type="radio"
                          id="formula-haskell"
                          name="formula"
                          value="haskell"
                          checked={inputs.formula === 'haskell'}
                          onChange={(e) => setInputs({...inputs, formula: 'haskell'})}
                          style={radioInputStyle}
                        />
                        <label htmlFor="formula-haskell" style={radioLabelStyle}>{t('formulaHaskell')}</label>
                      </div>
                      <div style={radioOptionStyle}>
                        <input
                          type="radio"
                          id="formula-tanaka"
                          name="formula"
                          value="tanaka"
                          checked={inputs.formula === 'tanaka'}
                          onChange={(e) => setInputs({...inputs, formula: 'tanaka'})}
                          style={radioInputStyle}
                        />
                        <label htmlFor="formula-tanaka" style={radioLabelStyle}>{t('formulaTanaka')}</label>
                      </div>
                      <div style={radioOptionStyle}>
                        <input
                          type="radio"
                          id="formula-nes"
                          name="formula"
                          value="nes"
                          checked={inputs.formula === 'nes'}
                          onChange={(e) => setInputs({...inputs, formula: 'nes'})}
                          style={radioInputStyle}
                        />
                        <label htmlFor="formula-nes" style={radioLabelStyle}>{t('formulaNes')}</label>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={formSectionStyle}>
                  <div style={formSectionTitleStyle}>
                    <span>📈</span>
                    <span>{t('methodSectionTitle')}</span>
                  </div>

                  <div style={formGroupStyle}>
                    <div style={radioGroupStyle}>
                      <div style={radioOptionStyle}>
                        <input
                          type="radio"
                          id="method-karvonen"
                          name="method"
                          value="karvonen"
                          checked={inputs.method === 'karvonen'}
                          onChange={(e) => setInputs({...inputs, method: 'karvonen'})}
                          style={radioInputStyle}
                        />
                        <label htmlFor="method-karvonen" style={radioLabelStyle}>{t('methodKarvonen')}</label>
                      </div>
                      <div style={radioOptionStyle}>
                        <input
                          type="radio"
                          id="method-percent"
                          name="method"
                          value="percent"
                          checked={inputs.method === 'percent'}
                          onChange={(e) => setInputs({...inputs, method: 'percent'})}
                          style={radioInputStyle}
                        />
                        <label htmlFor="method-percent" style={radioLabelStyle}>{t('methodPercent')}</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(245, 158, 11, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(245, 158, 11, 0.3)';
              }}
            >
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle} id="results-section">
              <h3 style={resultsH3Style}>{t('resultsTitle')}</h3>

              <div style={resultCardStyle}>
                <h4 style={resultCardH4Style}>
                  <span>📊</span>
                  <span>{t('summaryTitle')}</span>
                </h4>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('mhrResult')}</span>
                  <span style={resultValueStyle}>{results.mhr} {t('bpmUnit')}</span>
                </div>
                {results.rhr && (
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('rhrResult')}</span>
                    <span style={resultValueStyle}>{results.rhr} {t('bpmUnit')}</span>
                  </div>
                )}
                {results.reserve && (
                  <div style={{...resultItemStyle, marginBottom: 0}}>
                    <span style={resultLabelStyle}>{t('reserveResult')}</span>
                    <span style={resultValueStyle}>{results.reserve} {t('bpmUnit')}</span>
                  </div>
                )}
              </div>

              <div style={resultCardStyle}>
                <h4 style={resultCardH4Style}>
                  <span>🎯</span>
                  <span>{t('zonesTitle')}</span>
                </h4>
                <div style={zoneGridStyle}>
                  {results.zones.map((zone: any, index: number) => (
                    <div key={index} style={{...zoneCardStyle, borderLeftColor: zone.color}}>
                      <div style={zoneNameStyle}>{zone.name}</div>
                      <div style={zoneRangeStyle}>{zone.min}% - {zone.max}%</div>
                      <div style={zoneBpmStyle}>{zone.minHR} - {zone.maxHR} {t('bpmUnit')}</div>
                      <div style={{...zoneRangeStyle, marginTop: '0.5rem', fontSize: screenSize === 'mobile' ? '0.8rem' : '0.85rem'}}>{zone.description}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={infoTextStyle}>
                💡 <strong>{t('tipLabel')}</strong> {t('tipText')}
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}>{t('step1')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('step2')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('step3')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('step4')}</li>
          <li>{t('step5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}>{t('insight1')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('insight2')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('insight3')}</li>
          <li>{t('insight4')}</li>
        </ul>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem'}}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}>{t('detail1')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('detail2')}</li>
          <li>{t('detail3')}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '2rem'}}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}>{t('detail4')}</li>
          <li>{t('detail5')}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '2rem'}}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={{marginBottom: 0}}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={{...faqAnswerStyle, marginBottom: 0}}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={{...glassCardStyle, marginBottom: 0}}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://www.calculator.net/target-heart-rate-calculator.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://lluh.org/heart-vascular/our-services/adult-cardiology/heart-health-education/target-heart-rate-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://udshealth.com/target-heart-rate-and-maximum-heart-rate/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://www.heart.org/en/healthy-living/fitness/fitness-basics/target-heart-rates" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://www.nationaljewish.org/education/health-information/exercise-and-weight/target-heart-rate" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://www.mayoclinic.org/healthy-lifestyle/fitness/in-depth/exercise-intensity/art-20046887" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref6')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://health.clevelandclinic.org/exercise-heart-rate-zones-explained" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref7')}
            </a>
          </li>
          <li>
            <a href="https://healthy.kaiserpermanente.org/health-wellness/health-encyclopedia/he.interactive-tool-what-is-your-target-heart-rate.tx4374" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref8')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}